import { Injectable } from '@angular/core';
import { RouterService } from '@app/routing/router.service';
import { ReturnAddress } from '@app/shared/return-to/return-address';
import { BehaviorSubject, Subscription } from 'rxjs';
import { first, share, skip } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ReturnService {
  private _returnAddress$ = new BehaviorSubject<ReturnAddress | null>(null);

  returnAddress$ = this._returnAddress$.asObservable().pipe(share());

  private routerSubscription: Subscription | null = null;

  constructor(private routerService: RouterService) {}

  clearReturnAddress(): void {
    this._returnAddress$.next(null);
  }

  /**
   * Set the return address (will be shown before the page heading). The return address
   * will be cleared after the second navigation after this has been called. The
   * intention for this is, that the user sets the return address before navigating to
   * another page from where it should be possible to navigate bck to the return address.
   * If the user navigates away from there, then the return address will be cleared.
   *
   * @param address Address that the user can return to.
   * @param clearImmediately If true then the return address will be cleared
   * immediately after the next navigation. This is useful if a page wants to
   * set a return address by itself (e.g. the distribute function keys page uses
   * this, so the user can navigate back to the function keys of the device).
   */
  updateReturnAddress(address: ReturnAddress, clearImmediately: boolean = false): void {
    this._returnAddress$.next(address);

    if (this.routerSubscription != null) {
      this.routerSubscription.unsubscribe();
    }

    this.routerSubscription = this.routerService.routeChanged$
      .pipe(skip(clearImmediately ? 0 : 1))
      .subscribe(() => {
        this.updateReturnAddressAfterNavigation();
      });
  }

  private updateReturnAddressAfterNavigation(): void {
    this._returnAddress$.pipe(first()).subscribe((address) => {
      if (address != null) {
        this.clearReturnAddress();
      }
    });
  }
}
